import { errorToast } from "@/shared/utils/helpers";
import { RolesResponse } from "@/shared/utils/interfaces";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { get2 } from "../axios";
import useAuthHook from "./useAuthHook";

const useRoleHooks = (enable = true) => {
  const { isAuth } = useAuthHook();

  const getRoles = useQuery<RolesResponse>({
    queryKey: ["accountRoles"],
    queryFn: () => {
      return get2(`internal/secure/pos/v1/website-accounts/roles`);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: enable !== false && isAuth,
    placeholderData: keepPreviousData,
    throwOnError: (error) => {
      errorToast(error.message, "websiteAccountRolesError");
      return false;
    },
    retry: 1
  });

  const hasAuthority = useCallback(
    (role: string) => {
      if (getRoles?.data && role !== undefined) {
        return getRoles?.data?.roles?.includes(role);
      }
      return false;
    },
    [getRoles?.data]
  );

  const hasAdminAuthority = useMemo<boolean>(() => {
    return [
      "ROLE_APPSMEN_EMPLOYEE",
      "ROLE_SUPER_ADMIN",
      "ROLE_APPSMEN_ADMIN"
    ].some((role) => getRoles.data?.roles?.includes(role));
  }, [getRoles.data?.roles]);

  const isAppsmenEmployee = useMemo<boolean>(
    () => getRoles?.data?.roles?.includes("ROLE_APPSMEN_EMPLOYEE"),
    [getRoles?.data?.roles]
  );

  return {
    getRoles,
    hasAdminAuthority,
    isAppsmenEmployee,
    hasAuthority
  };
};

export default useRoleHooks;
